import React, { useState, useEffect, useMemo } from "react";
import logo from "../../images/logo_clinic4us.png";
import { sessionOptionsService, OptionItem } from "../../services/sessionOptionsService";
import { redirectToLogin } from "../../services/httpClient";
import UserAvatar from "../UserAvatar";
import ChatHeaderButton from "../chat/ChatHeaderButton";
import { openAssistants } from "../chat/chatDock";
import {
  Refresh,
  Logout,
  Menu as MenuIcon,
  Close as CloseIcon,
  FactCheck,
  ExpandLess,
  ExpandMore,
  Home,
  Groups,
  BarChart,
  Paid,
  MenuBook,
  SmartToy,
  Handshake,
  WhatsApp,
  FolderOutlined,
  CalendarMonth,
  EventAvailable,
  Bolt,
  Cake,
  PointOfSale,
  ReceiptLong,
  Today,
  MeetingRoom,
  AssignmentInd,
  ViewKanban,
  PeopleAlt,
  PersonAddAlt1,
  LocalHospital,
  Badge,
  MedicalServices,
  Description,
  EventBusy,
  Forum,
} from '@mui/icons-material';
import {
  AppBar,
  Toolbar,
  IconButton,
  Tooltip,
  Button,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  Menu,
  MenuItem,
  Collapse,
  ListItemIcon,
} from '@mui/material';
import { useAuth } from "../../contexts/AuthContext";

interface MenuItemProps {
  label: string;
  href?: string;
  onClick?: (e: React.MouseEvent) => void;
  children?: MenuItemProps[];
}

// Ícones dos agrupamentos do menu, por label (as categorias vêm do servidor como texto —
// RN-004d); categoria nova sem mapeamento cai no ícone neutro de pasta
const GROUP_ICONS: Record<string, React.ReactElement> = {
  'CRM': <Handshake fontSize="small" />,
  'Pacientes': <Groups fontSize="small" />,
  'Relatórios': <BarChart fontSize="small" />,
  'Financeiro': <Paid fontSize="small" />,
  // Divisão do antigo Administrativo (RN-004m)
  'Clínica': <LocalHospital fontSize="small" />,
  'Equipe e acesso': <Badge fontSize="small" />,
  'Serviços e convênios': <MedicalServices fontSize="small" />,
  'Prontuário e documentos': <Description fontSize="small" />,
  'Playbook': <MenuBook fontSize="small" />,
  'Tecnologia': <SmartToy fontSize="small" />,
  'Avaliações': <FactCheck fontSize="small" />,
};

const groupIcon = (label: string): React.ReactElement =>
  GROUP_ICONS[label] ?? <FolderOutlined fontSize="small" />;

// Ícones dos itens SOLTOS do primeiro nível (por href — o label pode ser renomeado na central)
// Atalhos do cabeçalho: mesmos ícones do menu (Home, CalendarMonth, PeopleAlt)
const QUICK_NAV: { page: string; label: string; icon: React.ReactElement }[] = [
  { page: 'dashboard', label: 'Dashboard', icon: <Home fontSize="small" /> },
  { page: 'schedule', label: 'Agenda', icon: <CalendarMonth fontSize="small" /> },
  { page: 'patients', label: 'Pacientes', icon: <PeopleAlt fontSize="small" /> },
];

// Etiqueta de ambiente (pedido do Hilton, 07/09/2026): vermelho no local e no túnel, âmbar em homologação,
// produção sem nada — faixa fina no topo + selo no centro, para não brigar com a marca.
const environmentBadge = (): { label: string; color: string; bg: string } | null => {
  const host = window.location.hostname.toLowerCase();
  if (host === 'localhost' || host === '127.0.0.1' || host.endsWith('.trycloudflare.com')) {
    return { label: 'LOCAL', color: '#c92a2a', bg: '#ffe3e3' };
  }
  if (host.startsWith('hml')) {
    return { label: 'HOMOLOGAÇÃO', color: '#9a6700', bg: '#fff3cd' };
  }
  return null;
};

const TOP_LEVEL_ICONS: Record<string, React.ReactElement> = {
  '?page=dashboard': <Home fontSize="small" />,
  '?page=schedule': <CalendarMonth fontSize="small" />,
  '?page=schedule-blocks': <EventBusy fontSize="small" />,
  '?page=opportunities': <EventAvailable fontSize="small" />,
  '?page=day-schedule': <Today fontSize="small" />,
  '?page=room-map': <MeetingRoom fontSize="small" />,
  '?page=crm-funnel': <ViewKanban fontSize="small" />,
  // Lista de pacientes subiu para o primeiro nível (RN-004k): item sem ícone aqui fica
  // visivelmente deslocado ao lado dos outros, que todos têm.
  '?page=patients': <PeopleAlt fontSize="small" />,
  '?page=assistants': <Forum fontSize="small" />,
};

// Ícones secundários de itens de submenu — os mesmos dos atalhos "Novo Paciente" da agenda,
// em cinza (hierarquia: grupo colorido, filho discreto). Sem mapeamento = só texto, como antes.
const CHILD_ICONS: Record<string, React.ReactElement> = {
  '?page=patients': <PeopleAlt fontSize="small" />,
  '?page=patient-quick-register': <Bolt fontSize="small" />,
  '?page=patient-register': <AssignmentInd fontSize="small" />,
  '?page=whatsapp-channels': <WhatsApp fontSize="small" />,
  '?page=assessment-forms': <FactCheck fontSize="small" />,
  '?page=birthdays': <Cake fontSize="small" />,
  '?page=patient-financial-page': <PointOfSale fontSize="small" />,
  '?page=report-receipts': <ReceiptLong fontSize="small" />,
  '?page=whatsapp-messages': <WhatsApp fontSize="small" />,
  '?page=day-schedule': <EventAvailable fontSize="small" />,
};

interface HeaderInternalProps {
  menuItems?: MenuItemProps[];
  showCTAButton?: boolean;
  ctaButtonText?: string;
  onCTAClick?: (e: React.MouseEvent) => void;
  onLogoClick?: () => void;
  className?: string;
  isLoggedIn?: boolean;
  userEmail?: string;
  userProfile?: string;
  clinicName?: string;
  notificationCount?: number;
  onNotificationClick?: () => void;
  onUserClick?: () => void;
  /** Sem efeito desde 29/08/2026: o bloqueio por inatividade é do AuthContext (SessionLockDialog). Aceito só para não quebrar as páginas que ainda passam. */
  onRevalidateLogin?: () => void;
  /** Header sem navegação (menu, notificações e perfil): telas de trabalho focado, como o
      preenchimento da avaliação, onde sair no meio custa caro. A logo continua clicável. */
  hideNavigation?: boolean;
  /** Ação à direita do header — a saída da tela quando a navegação está oculta. */
  rightAction?: React.ReactNode;
}

const HeaderInternal: React.FC<HeaderInternalProps> = ({
  menuItems,
  showCTAButton = false,
  ctaButtonText = "Ação",
  onCTAClick,
  onLogoClick,
  className = "",
  isLoggedIn = false,
  userEmail = "",
  userProfile = "",
  clinicName = "",
  notificationCount = 0,
  onNotificationClick,
  onUserClick,
  hideNavigation = false,
  rightAction,
}) => {
  const { user, updateProfile } = useAuth();
  const envBadge = useMemo(environmentBadge, []);

  // Usar dados do contexto se não forem passados por props
  const actualMenuItems = menuItems || user?.menuItems || [];

  // A seção "Meus agentes" saiu do menu em 07/09/2026: os agentes vivem no chat (ChatLauncher), que já
  // respeita a permissão `chat` — pelo menu, a Diretoria chegava à Ani sem ter o chat.
  // "Cadastro completo" (patient-register) abre o formulário em branco de paciente novo. O Profissional
  // precisa da PÁGINA (é como abre o prontuário pela lista), mas não do item de menu (Hilton, 07/09/2026).
  // Tirar o grant bloquearia o prontuário; por isso o corte é só no menu.
  const isProfessionalRole = (user?.role || "").toLowerCase() === "profissional";
  const menuWithAgents = useMemo(() => {
    const base = actualMenuItems as unknown as MenuItemProps[];
    if (!isProfessionalRole) return base;
    return base
      .map((item) => (item.children
        ? { ...item, children: item.children.filter((c) => c.href !== "?page=patient-register") }
        : item))
      .filter((item) => !(item.children && item.children.length === 0 && !item.href));
  }, [actualMenuItems, isProfessionalRole]);

  // Submenus (itens com children) abertos/fechados no drawer
  const [addPatientAnchor, setAddPatientAnchor] = useState<null | HTMLElement>(null);
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({});
  const toggleGroup = (label: string) =>
    setExpandedGroups((prev) => ({ ...prev, [label]: !prev[label] }));

  // RN-004h: o PRIMEIRO agregador do menu (hoje "Pacientes", logo abaixo do
  // Dashboard) inicia expandido; os demais fechados. Só define o estado inicial —
  // o toggle do usuário prevalece depois.
  useEffect(() => {
    // menuItems (prop) e user.menuItems têm tipos distintos — normaliza para o shape comum
    const groups = actualMenuItems as Array<{ label: string; children?: unknown[] }>;
    const firstGroup = groups.find((item) => Array.isArray(item.children) && item.children.length > 0);
    if (!firstGroup) return;
    setExpandedGroups((prev) =>
      prev[firstGroup.label] === undefined ? { ...prev, [firstGroup.label]: true } : prev
    );
  }, [actualMenuItems]);
  const actualUserEmail = userEmail || user?.email || '';
  const actualUserProfile = userProfile || user?.role || '';
  const actualClinicName = clinicName || user?.clinicName || '';
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isChangeProfileModalOpen, setIsChangeProfileModalOpen] = useState(false);
  const [selectedEntity, setSelectedEntity] = useState('');
  const [selectedProfile, setSelectedProfile] = useState('');
  const [entityOptions, setEntityOptions] = useState<OptionItem[]>([]);
  const [profileOptions, setProfileOptions] = useState<OptionItem[]>([]);
  const [loadingEntities, setLoadingEntities] = useState(false);
  const [loadingProfiles, setLoadingProfiles] = useState(false);
  // Cleanup mobile menu state when component unmounts
  useEffect(() => {
    return () => {
      document.body.classList.remove('mobile-menu-open');
    };
  }, []);



  const toggleMobileMenu = () => {
    const newState = !isMobileMenuOpen;
    setIsMobileMenuOpen(newState);

    // Add/remove class to body to hide hamburger when menu is open
    if (newState) {
      document.body.classList.add('mobile-menu-open');
      console.log('Mobile menu opened - added class mobile-menu-open');
    } else {
      document.body.classList.remove('mobile-menu-open');
      console.log('Mobile menu closed - removed class mobile-menu-open');
    }
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
    document.body.classList.remove('mobile-menu-open');
  };

  const handleLogoClick = () => {
    if (onLogoClick) {
      onLogoClick();
    } else if (isLoggedIn) {
      window.location.href = `${window.location.origin}/?page=dashboard`;
    } else {
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    }
  };

  const handleMenuItemClick = (item: MenuItemProps, e: React.MouseEvent) => {
    // Assistentes é um app em modal por cima da tela atual: abre sem navegar (sem recarregar a página)
    if (item.href === '?page=assistants' && !e.ctrlKey && !e.metaKey) {
      e.preventDefault();
      openAssistants();
    }
    if (item.onClick) {
      item.onClick(e);
    }
    closeMobileMenu();
  };

  const truncateEmail = (email: string, maxLength: number = 25): string => {
    if (email.length <= maxLength) {
      return email;
    }
    return email.substring(0, maxLength) + '...';
  };

  // Carrega os perfis do usuário na entidade; se houver só um, já pré-seleciona.
  const loadProfileOptions = async (entityId: string) => {
    if (!user?.userId || !entityId) {
      setProfileOptions([]);
      setSelectedProfile('');
      return;
    }
    setLoadingProfiles(true);
    try {
      const profiles = await sessionOptionsService.getProfilesByUserEntity(user.userId, entityId);
      setProfileOptions(profiles);
      setSelectedProfile(profiles.length === 1 ? profiles[0].id : '');
    } catch (error) {
      console.error('Erro ao carregar perfis:', error);
      setProfileOptions([]);
    } finally {
      setLoadingProfiles(false);
    }
  };

  const handleOpenChangeProfileModal = async () => {
    setIsChangeProfileModalOpen(true);
    setSelectedEntity('');
    setSelectedProfile('');
    setProfileOptions([]);
    closeMobileMenu();

    if (!user?.userId) return;
    setEntityOptions([]);
    setLoadingEntities(true);
    try {
      const entities = await sessionOptionsService.getEntitiesByUser(user.userId);
      setEntityOptions(entities);
      // Se o usuário tem só uma entidade, já preenche entidade e busca o(s) perfil(is).
      if (entities.length === 1) {
        setSelectedEntity(entities[0].id);
        await loadProfileOptions(entities[0].id);
      }
    } catch (error) {
      console.error('Erro ao carregar entidades:', error);
      setEntityOptions([]);
    } finally {
      setLoadingEntities(false);
    }
  };

  const handleEntityChange = async (entityId: string) => {
    setSelectedEntity(entityId);
    setSelectedProfile('');
    await loadProfileOptions(entityId);
  };

  const handleCloseChangeProfileModal = () => {
    setIsChangeProfileModalOpen(false);
    setSelectedEntity('');
    setSelectedProfile('');
    setProfileOptions([]);
  };

  const handleConfirmChangeProfile = () => {
    if (!selectedEntity || !selectedProfile) {
      alert('Por favor, selecione uma entidade e um perfil.');
      return;
    }

    try {
      // Usar função do AuthContext para atualizar perfil
      updateProfile(selectedEntity, selectedProfile);

      // Fechar modal - o updateProfile já recarrega a página
      handleCloseChangeProfileModal();
    } catch (error) {
      console.error('Erro ao mudar perfil:', error);
      alert('Erro ao alterar perfil. Tente novamente.');
    }
  };

  return (
    <AppBar
      position="fixed"
      className={`header-internal ${className} ${isLoggedIn ? 'logged-in' : ''}`}
      elevation={0}
      sx={{
        backgroundColor: isLoggedIn ? '#fff' : 'rgba(255, 255, 255, 0.95)',
        backdropFilter: 'blur(10px)',
        borderBottom: '1px solid rgba(226, 232, 240, 0.8)',
        height: '85px',
        zIndex: 1000,
        boxShadow: isLoggedIn ? '0 2px 10px rgba(0, 0, 0, 0.1)' : 'none',
      }}
    >
      {envBadge && <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: 4, bgcolor: envBadge.color }} />}
      <Toolbar sx={{ height: '85px', px: { xs: '6px', sm: '6px' }, justifyContent: 'space-between' }}>
        {/* Menu e logo — mesma posição e tamanhos logado ou não (pedido do Hilton, 29/08) */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pl: hideNavigation ? '1rem' : 0 }}>
          {!hideNavigation && (
            <IconButton
              onClick={toggleMobileMenu}
              aria-label="Toggle menu"
              sx={{
                color: '#03B4C6',
                display: { xs: 'flex', md: 'flex' }
              }}
            >
              <MenuIcon sx={{ fontSize: '2.5rem' }} />
            </IconButton>
          )}

          <Box
            component="img"
            src={logo}
            alt="CLINIC4US"
            onClick={handleLogoClick}
            sx={{
              height: { xs: '28px', sm: '32px' },
              cursor: 'pointer',
              objectFit: 'contain'
            }}
          />
        </Box>

        {envBadge && (
          <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center', px: 1 }}>
            <Box sx={{ px: 1.25, py: 0.25, borderRadius: 10, bgcolor: envBadge.bg, color: envBadge.color, fontSize: '0.7rem', fontWeight: 800, letterSpacing: '0.08em', border: `1px solid ${envBadge.color}55`, whiteSpace: 'nowrap' }}>
              {envBadge.label}
            </Box>
          </Box>
        )}

        {/* Ações do Header */}
        {/* Respiro à direita: sem ele o avatar encosta na borda e some meio círculo (achado do Hilton, 07/09) */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 1.5 }, pr: hideNavigation ? '1rem' : { xs: 0.5, sm: 1.5 }, flexShrink: 0 }}>
          {rightAction}
          {isLoggedIn && !hideNavigation && (
            // Menu rápido (pedido do Hilton, 07/09): Dashboard, Agenda e Pacientes com os mesmos ícones do
            // menu, só para páginas que o perfil tem. Some no celular, onde o cabeçalho não tem largura.
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
              {QUICK_NAV.filter((q) => (user?.allowedPages || []).includes(q.page)).map((q) => (
                <Tooltip key={q.page} title={q.label}>
                  <IconButton
                    component="a"
                    href={`?page=${q.page}`}
                    aria-label={q.label}
                    size="small"
                    sx={{ display: { xs: 'none', md: 'inline-flex' }, color: '#8a94a6', '&:hover': { bgcolor: '#f1f3f5', color: '#03B4C6' } }}
                  >
                    {q.icon}
                  </IconButton>
                </Tooltip>
              ))}
              {/* Incluir paciente direto do cabeçalho (pedido do Hilton, 07/09): as mesmas duas
                  opções da lista de pacientes. O atalho de Pacientes continua indo para a lista —
                  este botão ACRESCENTA a inclusão, não a substitui.
                  A condição é `patient-create`, e NÃO "tem a tela de cadastro" (RN-112p): a tela
                  `patient-register` é a mesma que ABRE o prontuário de um paciente existente, então
                  perguntar por ela mostrava o botão de incluir a quem só podia consultar. */}
              {(user?.allowedPages || []).includes('patient-create') && (
                <>
                  <Tooltip title="Incluir paciente">
                    <IconButton
                      onClick={(e) => setAddPatientAnchor(e.currentTarget)}
                      aria-label="Incluir paciente"
                      size="small"
                      sx={{ display: { xs: 'none', md: 'inline-flex' }, color: '#8a94a6', '&:hover': { bgcolor: '#f1f3f5', color: '#03B4C6' } }}
                    >
                      <PersonAddAlt1 fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Menu
                    anchorEl={addPatientAnchor}
                    open={addPatientAnchor !== null}
                    onClose={() => setAddPatientAnchor(null)}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                  >
                    {(user?.allowedPages || []).includes('patient-quick-register') && (
                      <MenuItem
                        onClick={() => {
                          setAddPatientAnchor(null);
                          window.location.href = '?page=patient-quick-register';
                        }}
                      >
                        <ListItemIcon><Bolt fontSize="small" /></ListItemIcon>
                        <ListItemText primary="Cadastro rápido" secondary="Dados mínimos, em um modal" />
                      </MenuItem>
                    )}
                    {(user?.allowedPages || []).includes('patient-register') && (
                      <MenuItem
                        onClick={() => {
                          setAddPatientAnchor(null);
                          window.location.href = '?page=patient-register';
                        }}
                      >
                        <ListItemIcon><AssignmentInd fontSize="small" /></ListItemIcon>
                        <ListItemText primary="Cadastro completo" secondary="Página com todos os dados" />
                      </MenuItem>
                    )}
                  </Menu>
                </>
              )}

              {/* O chat fica na mesma fileira e com o mesmo espaçamento dos atalhos */}
              <ChatHeaderButton />
            </Box>
          )}
          {isLoggedIn && !hideNavigation ? (
            <Box
              component="button"
              type="button"
              onClick={() => window.location.href = '?page=user-profile'}
              aria-label="Meu perfil"
              title="Meu perfil"
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.25,
                border: 0,
                background: 'transparent',
                cursor: 'pointer',
                p: 0,
                textAlign: 'right',
              }}
            >
              <Box sx={{ display: { xs: 'none', md: 'flex' }, flexDirection: 'column', lineHeight: 1.2 }}>
                <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#2D3748' }}>
                  {truncateEmail(actualClinicName, 28)}
                </Typography>
                <Typography sx={{ fontSize: '0.72rem', color: '#6c757d' }}>
                  {actualUserProfile}
                </Typography>
              </Box>
              <Box sx={{ flexShrink: 0, display: 'flex' }}><UserAvatar label={actualUserEmail} size={36} /></Box>
            </Box>
          ) : (
            <>
              {showCTAButton && (
                <Button
                  onClick={onCTAClick}
                  variant="contained"
                  sx={{
                    display: { xs: 'none', sm: 'inline-flex' },
                    backgroundColor: '#03B4C6',
                    color: 'white',
                    textTransform: 'none',
                    px: 3,
                    '&:hover': {
                      backgroundColor: '#029AAB',
                    }
                  }}
                >
                  {ctaButtonText}
                </Button>
              )}
            </>
          )}
        </Box>

      </Toolbar>

      {/* Menu Mobile usando Drawer */}
      <Drawer
        anchor="left"
        open={isMobileMenuOpen}
        onClose={closeMobileMenu}
        sx={{
          '& .MuiDrawer-paper': {
            width: isLoggedIn ? '320px' : '280px',
            backgroundColor: '#fff',
          }
        }}
      >
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          {/* Header do Menu Mobile */}
          {isLoggedIn && (
            <Box sx={{ p: 2, backgroundColor: '#f8f9fa', borderBottom: '1px solid #e9ecef' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                <UserAvatar
                  label={actualUserEmail}
                  size={40}
                  title="Meu perfil"
                  onClick={() => {
                    window.location.href = '?page=user-profile';
                    closeMobileMenu();
                  }}
                />
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#2D3748' }}>
                    {truncateEmail(actualUserEmail)}
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#6c757d' }}>
                    {actualUserProfile}
                  </Typography>
                  <Typography variant="caption" sx={{ display: 'block', color: '#6c757d' }}>
                    {truncateEmail(actualClinicName, 20)}
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  startIcon={<Refresh />}
                  onClick={handleOpenChangeProfileModal}
                  size="small"
                  fullWidth
                  variant="outlined"
                  sx={{
                    textTransform: 'none',
                    fontSize: '0.75rem',
                    borderColor: '#03B4C6',
                    color: '#03B4C6',
                    '&:hover': {
                      borderColor: '#029AAB',
                      backgroundColor: 'rgba(3, 180, 198, 0.04)',
                    }
                  }}
                >
                  Mudar perfil
                </Button>
                <Button
                  startIcon={<Logout />}
                  onClick={() => {
                    localStorage.removeItem('clinic4us-token');
                    localStorage.removeItem('clinic4us-user-session');
                    localStorage.removeItem('clinic4us-remember-me');
                    redirectToLogin();
                  }}
                  size="small"
                  fullWidth
                  variant="outlined"
                  color="error"
                  sx={{
                    textTransform: 'none',
                    fontSize: '0.75rem',
                  }}
                >
                  Sair
                </Button>
              </Box>
            </Box>
          )}

          {/* Menu Items - Carregado dinamicamente do perfil do usuário */}
          {/* Respiro no fim (pedido do Hilton, 07/09): o último item encostava na borda do
              drawer e, no celular, ficava embaixo da barra do sistema — difícil de tocar. */}
          <List sx={{ flex: 1, pt: 1, pb: 5, overflowY: 'auto' }}>
            {menuWithAgents.map((item, index) =>
              item.children && item.children.length > 0 ? (
                <React.Fragment key={index}>
                  <ListItem
                    onClick={() => toggleGroup(item.label)}
                    sx={{
                      cursor: 'pointer',
                      color: '#2D3748',
                      '&:hover': { backgroundColor: 'rgba(3, 180, 198, 0.08)' }
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 34, color: '#03B4C6' }}>
                      {groupIcon(item.label)}
                    </ListItemIcon>
                    <ListItemText
                      primary={item.label}
                      primaryTypographyProps={{ fontWeight: 700 }}
                    />
                    {expandedGroups[item.label] ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                  </ListItem>
                  <Collapse in={!!expandedGroups[item.label]} timeout="auto" unmountOnExit>
                    <List disablePadding>
                      {item.children.map((child, childIndex) => (
                        <ListItem
                          key={childIndex}
                          component="a"
                          href={child.href || "#"}
                          onClick={(e) => handleMenuItemClick(child, e)}
                          sx={{
                            cursor: 'pointer',
                            color: '#2D3748',
                            pl: 4,
                            '&:hover': { backgroundColor: 'rgba(3, 180, 198, 0.08)' }
                          }}
                        >
                          {child.href && CHILD_ICONS[child.href] && (
                            <ListItemIcon sx={{ minWidth: 30, color: '#6c757d' }}>
                              {CHILD_ICONS[child.href]}
                            </ListItemIcon>
                          )}
                          <ListItemText primary={child.label} primaryTypographyProps={{ fontSize: 14 }} />
                        </ListItem>
                      ))}
                    </List>
                  </Collapse>
                </React.Fragment>
              ) : (
                <ListItem
                  key={index}
                  component="a"
                  href={item.href || "#"}
                  onClick={(e) => handleMenuItemClick(item, e)}
                  sx={{
                    cursor: 'pointer',
                    color: '#2D3748',
                    '&:hover': {
                      backgroundColor: 'rgba(3, 180, 198, 0.08)',
                    }
                  }}
                >
                  {item.href && TOP_LEVEL_ICONS[item.href] && (
                    <ListItemIcon sx={{ minWidth: 34, color: '#03B4C6' }}>
                      {TOP_LEVEL_ICONS[item.href]}
                    </ListItemIcon>
                  )}
                  <ListItemText primary={item.label} />
                </ListItem>
              )
            )}
          </List>

          {/* CTA Button for Non-Logged Users */}
          {!isLoggedIn && showCTAButton && (
            <Box sx={{ p: 2, borderTop: '1px solid #e9ecef' }}>
              <Button
                onClick={(e) => {
                  if (onCTAClick) onCTAClick(e);
                  closeMobileMenu();
                }}
                variant="contained"
                fullWidth
                sx={{
                  backgroundColor: '#03B4C6',
                  color: 'white',
                  textTransform: 'none',
                  '&:hover': {
                    backgroundColor: '#029AAB',
                  }
                }}
              >
                {ctaButtonText}
              </Button>
            </Box>
          )}
        </Box>
      </Drawer>


      {/* Modal de Mudança de Perfil */}
      <Dialog
        open={isChangeProfileModalOpen}
        onClose={handleCloseChangeProfileModal}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: '12px' }
        }}
      >
        <DialogTitle
          sx={{
            backgroundColor: '#03B4C6', color: '#ffffff', padding: '1.5rem',
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Refresh sx={{ fontSize: '1.75rem' }} />
            <Typography variant="h6" component="h3" sx={{ fontSize: '1.25rem', fontWeight: 600 }}>
              Mudar Perfil
            </Typography>
          </Box>
          <IconButton onClick={handleCloseChangeProfileModal} sx={{ color: '#ffffff', padding: '0.25rem' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: '24px !important' }}>
          <Typography variant="body2" sx={{ color: '#6c757d', mb: 2 }}>
            Selecione a entidade e o perfil para o qual deseja alterar.
          </Typography>
          <TextField
            select
            label="Entidade"
            value={selectedEntity}
            onChange={(e) => handleEntityChange(e.target.value)}
            fullWidth
            disabled={loadingEntities}
            InputLabelProps={{ shrink: true }}
            helperText={loadingEntities ? 'Carregando entidades...' : ' '}
            sx={{ mb: 1 }}
          >
            <MenuItem value="">
              {loadingEntities ? 'Carregando...' : 'Selecione uma entidade'}
            </MenuItem>
            {entityOptions.map((entity) => (
              <MenuItem key={entity.id} value={entity.id}>{entity.name}</MenuItem>
            ))}
          </TextField>

          <TextField
            select
            label="Perfil"
            value={selectedProfile}
            onChange={(e) => setSelectedProfile(e.target.value)}
            fullWidth
            disabled={!selectedEntity || loadingProfiles}
            InputLabelProps={{ shrink: true }}
            helperText={loadingProfiles ? 'Carregando perfis...' : ' '}
          >
            <MenuItem value="">
              {loadingProfiles ? 'Carregando...' : 'Selecione um perfil'}
            </MenuItem>
            {profileOptions.map((profile) => (
              <MenuItem key={profile.id} value={profile.id}>{profile.name}</MenuItem>
            ))}
          </TextField>
        </DialogContent>
        <Box sx={{ p: 2, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
          <Button
            onClick={handleCloseChangeProfileModal}
            variant="outlined"
            sx={{ textTransform: 'none' }}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleConfirmChangeProfile}
            variant="contained"
            disabled={!selectedEntity || !selectedProfile || loadingEntities || loadingProfiles}
            sx={{
              textTransform: 'none',
              backgroundColor: '#03B4C6',
              '&:hover': { backgroundColor: '#029AAB' },
              '&.Mui-disabled': { backgroundColor: '#03B4C6', opacity: 0.5, color: 'white' }
            }}
          >
            Confirmar
          </Button>
        </Box>
      </Dialog>
    </AppBar>
  );
};

export default HeaderInternal;
